'use client';

import type { Slider } from '@/types';

import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'react-feather';
import { Autoplay } from 'swiper/modules';
import { Swiper, type SwiperRef, SwiperSlide } from 'swiper/react';

import 'swiper/css';

import { Box, Flex, Icon, IconButton, Image, Link } from '@/components';
import HomeSliderOverlayDesktop from '@/svgs/homeSliderOverlayDesktop.svg';
import HomeSliderOverlayMobile from '@/svgs/homeSliderOverlayMobile1.svg';

const LandingSlider: React.FC<{ sliders: Slider[] }> = ({ sliders }) => {
  const swiperRef = useRef<SwiperRef>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  const handleSlideChange = () => {
    const swiper = swiperRef.current?.swiper;
    if (swiper) {
      setIsBeginning(swiper.isBeginning);
      setIsEnd(swiper.isEnd);
    }
  };

  useEffect(() => {
    handleSlideChange();
  }, []);

  const prevSlide = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    swiperRef.current?.swiper.slidePrev();
  };

  const nextSlide = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    swiperRef.current?.swiper.slideNext();
  };

  return (
    <Box maxW="1440px" mx="auto" position="relative" zIndex={0}>
      <Swiper
        ref={swiperRef}
        dir="rtl"
        modules={[Autoplay]}
        autoplay={true}
        speed={500}
        spaceBetween={0}
        onSlideChange={handleSlideChange}
      >
        {sliders?.map((slide, index) => (
          <SwiperSlide key={slide.id}>
            <Link
              href={slide.link}
              target="_blank"
              position="relative"
              width="full"
              height="auto"
              display="block"
            >
              <Box
                display={{ base: 'none', md: 'block' }}
                width="full"
                position="relative"
                aspectRatio="1920/800"
              >
                <Image
                  alt={slide.alt_image}
                  src={slide.image}
                  fill
                  sizes="(min-width: 90em) 1440px, 100vw"
                  loading={index === 0 ? 'eager' : 'lazy'}
                />
                <Box
                  w="full"
                  h="auto"
                  position="absolute"
                  bottom="-1px"
                  right="-2px"
                  left="-2px"
                >
                  <HomeSliderOverlayDesktop width="101%" />
                </Box>
              </Box>

              <Box
                display={{ base: 'block', md: 'none' }}
                width="full"
                position="relative"
                aspectRatio="750/1000"
              >
                <Image
                  alt={slide.alt_image}
                  src={slide.portrait_image}
                  fill
                  sizes="(min-width: 90em) 1440px, 100vw"
                  loading={index === 0 ? 'eager' : 'lazy'}
                />
                <Box
                  w="full"
                  h="auto"
                  position="absolute"
                  bottom={'-1'}
                  right={'-1px'}
                  left={'-1px'}
                >
                  <HomeSliderOverlayMobile width="101%" />
                </Box>
              </Box>
            </Link>
          </SwiperSlide>
        ))}
      </Swiper>
      <Flex
        gap={'10px'}
        position={'absolute'}
        left={'50%'}
        bottom="48px"
        transform="translateX(-50%)"
        zIndex={2}
      >
        <IconButton
          backgroundColor={'#FFFFFF4D'}
          borderRadius="8px"
          icon={
            <Icon boxSize="8" opacity={1} as={ChevronRight} stroke={'text'} />
          }
          disabled={isBeginning}
          variant={'ghost'}
          aria-label="prev slide"
          onClick={prevSlide}
          zIndex={2}
        />
        <IconButton
          backgroundColor={'#FFFFFF4D'}
          borderRadius="8px"
          disabled={isEnd}
          aria-label="next slide"
          variant={'ghost'}
          onClick={nextSlide}
          zIndex={2}
          icon={<Icon boxSize="8" as={ChevronLeft} stroke={'text'} />}
        />
      </Flex>
    </Box>
  );
};

export default LandingSlider;
